import React, { useState, useEffect, ChangeEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CursorIcon from 'calcite-ui-icons-react/CursorIcon';
import { useToasts } from 'react-toast-notifications';

import DrawTools from './draw-tools';
import RadioButton from './radio-button';
import Icon from '../../../assets';
import Loader from '../loader';

import MapController from '../../../controllers/MapController';
import SketchController from '../../../controllers/SketchContoller';

import { RootState } from '../../../reducers/app';
import { setCreatingRiverBoundaries, setBufferDistance, setBufferUnit } from '../../../reducers/auditdata/actions';

import './contextual-data.scss';
import { leftpanel } from '../../../config/leftpanel.config';

/**
 * Component enabling a user to add contextual data
 */
const ContextualData = () => {
  // LOCAL STATE
  const [includeHydrology, setIncludeHydrology] = useState(false);
  const [validBufferDistance, setValidBufferDistance] = useState(true);

  // REDUX
  const creatingRiverBoundaries = useSelector((state: RootState) => state.auditdata.creatingRiverBoundaries);
  const loaderActive = useSelector((state: RootState) => state.leftpanel.loader);
  const bufferUnit = useSelector((state: RootState) => state.auditdata.buffer.unit);
  const bufferDistance = useSelector((state: RootState) => state.auditdata.buffer.distance);
  const visibleLayers = useSelector((state: RootState) => state.mapview.visibleLayers);
  const riverSketchToolActive = useSelector((state: RootState) => state.mapview.riverSketchToolActive);
  const auditArea = useSelector((state: RootState) => state.auditdata.auditArea.graphic);
  const dispatch = useDispatch();

  const { addToast } = useToasts();

  /**
   * Handles distance unit change and updates the buffer distance appropriately
   * @param unit Either feet or meters
   */
  const handleUnitChange = (unit: 'feet' | 'meters') => {
    switch (unit) {
      case 'feet':
        dispatch(setBufferDistance(Math.round(bufferDistance / 0.3048)));
        break;
      default:
        dispatch(setBufferDistance(Math.round(bufferDistance * 0.3048)));
    }
    dispatch(setBufferUnit(unit));
  };

  /**
   * Handles buffer distance value changes
   * @param evt The event object from changing the buffer distance value
   */
  const handleBufferDistance = (evt: ChangeEvent<HTMLInputElement>) => {
    let value: number = Number(evt.target.value);
    let minValue: number = Number(evt.target.min);
    let maxValue: number = Number(evt.target.max);

    if (value < minValue || value > maxValue) {
      setValidBufferDistance(false);
    } else {
      setValidBufferDistance(true);
    }
    dispatch(setBufferDistance(value));
  };

  /**
   * Function that initializes River Boundary selection
   */
  const handleClickRiverBoundaries = (selecting: boolean) => {
    dispatch(setCreatingRiverBoundaries(selecting));
    if (MapController._riverLayer) {
      MapController.updateLayerVisibility(MapController._riverLayer.id, selecting);
    }

    if (riverSketchToolActive) {
      SketchController.toggleRiverSketchTool(false);
    }

    if (!visibleLayers.includes('hydrology')) {
      MapController.updateLayerVisibility('hydrology', true);
    }
  };

  /**
   * Function that handles toggling whether or not a user is including Hydrology
   */
  const handleIncludeHydrologyToggle = () => {
    setIncludeHydrology(!includeHydrology);
    MapController.updateLayerVisibility('hydrology', !includeHydrology);
  };

  /**
   * Function that handles buffering river boundaries
   */
  const handleCreateRiverBoundaries = () => {
    if (bufferDistance > 100000) {
      addToast('The selected buffer distance is higher than the maximum 100,000', {
        appearance: 'error',
        autoDismiss: true,
      });
    } else {
      MapController.generateContextualRiverBoundaryLayer(includeHydrology);
    }
  };

  useEffect(() => {
    setIncludeHydrology(visibleLayers.includes('hydrology')); // fire this off initially so it's in-sync with hydrology visibility
  }, [visibleLayers]);

  return (
    <div>
      {loaderActive && <Loader />}
      <p className='left-panel__description'>
        {creatingRiverBoundaries
          ? 'Create riparian boundaries'
          : 'Draw or upload additional contextual data to be used in the audit. These will appear in the "My Layers" section.'}
      </p>
      {creatingRiverBoundaries ? (
        <React.Fragment>
          <Icon name='contextual-data' className='contextual-data-icon' />
          <div className='contextual-data__buffer'>
            <label>Buffer Distance:</label>
            <input
              type='number'
              min={1}
              max={bufferUnit === 'meters' ? leftpanel.maxBufferDistanceMeters : leftpanel.maxBufferDistanceFeet}
              value={bufferDistance}
              onChange={(evt) => handleBufferDistance(evt)}
            />
          </div>
          <div className='contextual-data__buffer-metric'>
            <RadioButton
              value={'meters'}
              label={'Meters'}
              checked={bufferUnit}
              onRadioChange={(unit) => handleUnitChange(unit as 'meters')}
            />
            <RadioButton
              value={'feet'}
              label={'Feet'}
              checked={bufferUnit}
              onRadioChange={(unit) => handleUnitChange(unit as 'feet')}
            />
          </div>
          <div className='contextual-date-hydro-toggle'>
            <input type='checkbox' id='hydrology' checked={includeHydrology} onChange={handleIncludeHydrologyToggle} />
            <label htmlFor={'hydrology'}>Use Default Hydrology Layer</label>
          </div>
          <button
            className={`contextual-data-add-river ${riverSketchToolActive ? 'active' : ''}`}
            onClick={() => SketchController.toggleRiverSketchTool(!riverSketchToolActive)}
          >
            {riverSketchToolActive ? 'Cancel' : 'Add Stream Centerline'}
          </button>
          <div className='contextual-data__actions'>
            <button
              disabled={!validBufferDistance}
              className='contextual-data-buffer'
              onClick={() => handleCreateRiverBoundaries()}
            >
              <p>Buffer</p>
            </button>
            <button className='contextual-data-cancel' onClick={() => handleClickRiverBoundaries(false)}>
              <p>Cancel</p>
            </button>
          </div>
          <span className='contextual-data__buffer-error'>
            {!validBufferDistance &&
              bufferUnit === 'meters' &&
              bufferDistance > leftpanel.maxBufferDistanceMeters &&
              `Buffer distance must be smaller than 5000 meters`}
            {!validBufferDistance &&
              bufferUnit === 'feet' &&
              bufferDistance > leftpanel.maxBufferDistanceFeet &&
              `Buffer distance must be smaller than 16500 feet`}
            {!validBufferDistance && bufferDistance < 1 && `Buffer distance must be greater than 0`}
          </span>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <button
            className='contextual-data-button'
            disabled={!auditArea}
            onClick={() => handleClickRiverBoundaries(true)}
          >
            <p>Create Riparian Boundaries</p>
            <CursorIcon />
          </button>
          <DrawTools dataType={'contextual'} />
        </React.Fragment>
      )}
    </div>
  );
};

export default ContextualData;
